import {ContentGrid} from '@app/web-player/playable-item/content-grid';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';
import React, { Fragment, useMemo } from 'react';
import {ChannelContentProps} from '@app/web-player/channels/channel-content';
import {ChannelContentGridItem} from '@app/web-player/channels/channel-content-grid-item';
import {ChannelHeading} from '@app/web-player/channels/channel-heading';
import {useChannelContent} from '@common/channels/requests/use-channel-content';
import {ChannelContentModel} from '@app/admin/channels/channel-content-config';
import {useInfiniteChannelContent} from '@common/channels/requests/use-infinite-channel-content';
import clsx from 'clsx';
import {
    PaginationControls,
    PaginationControlsType,
} from '@common/ui/navigation/pagination-controls';

import { AlbumListItem } from '@app/web-player/albums/album-list/album-list-item';
import { Album } from '@app/web-player/albums/album';

import { useSearchParams } from 'react-router';
import { useExtendedInfiniteChannelContent } from '@player/custom/hooks/use-extended-infinite-channel-content';

export function ChannelContentGrid(props: ChannelContentProps) {
    const isInfiniteScroll =
        !props.isNested &&
        (!props.channel.config.paginationType ||
            props.channel.config.paginationType === 'infiniteScroll');
    return (
        <Fragment>
            <ChannelHeading {...props} />
            {isInfiniteScroll ? (
                <InfiniteScrollGrid {...props} />
            ) : (
                <PaginatedGrid {...props} />
            )}
        </Fragment>
    );
}

function PaginatedGrid({channel, isNested}: ChannelContentProps) {
    const [searchParams] = useSearchParams();

    const shouldPaginate = useMemo(() => !isNested, [isNested]);
    const query = useChannelContent(channel, channel?.config?.contentModel !== 'artist' ? {
        with: 'tracks,artists,genres,tags,tracks.artists',
        withCount: 'tracks',
        query: searchParams.get('query') || ''
    } : {
        query: searchParams.get('query') || ''
    }, {isNested});

    const isListview = useMemo(() => {
        return searchParams.get('view') === 'list'
    }, [searchParams])

    return (
        <div
            className={clsx(
                'transition-opacity',
                query.isPlaceholderData && 'opacity-70',
            )}
        >
            {isListview ? (
                <div className="space-y-32">
                    {query.data?.data.map((album, index) => (
                        <AlbumListItem
                            key={`${album.id}-${album.model_type}`}
                            album={album as Album}
                        />
                    ))}
                </div>
            ) : (
                <ContentGrid>
                    {query.data?.data.map(item => (
                        <ChannelContentGridItem
                            key={`${item.id}-${item.model_type}`}
                            item={item}
                            items={query.data?.data}
                        />
                    ))}
                </ContentGrid>
            )}


            {shouldPaginate && (
                <PaginationControls
                    pagination={query.data}
                    type={channel.config.paginationType as PaginationControlsType}
                    className="mt-24"
                    scrollToTop
                />
            )}
        </div>
    );
}

function InfiniteScrollGrid({channel}: ChannelContentProps) {
    const [searchParams] = useSearchParams();

    const query = useExtendedInfiniteChannelContent<ChannelContentModel>(channel, channel?.config?.contentModel !== 'artist' ? {
        with: 'tracks,artists,genres,tags',
        withCount: 'tracks',
        query: searchParams.get('query') || ''
    } : {
        query: searchParams.get('query') || ''
    });

    const isListview = useMemo(() => {
        return searchParams.get('view') === 'list'
    }, [searchParams])

    return (
        <div>
            {isListview ? (
                <div className="space-y-32">
                    {query.items.map((album) => (
                        <AlbumListItem
                            key={album.id}
                            album={album as Album}
                        />
                    ))}
                </div>
            ) : (
                <ContentGrid>
                    {query.items.map(item => (
                        <ChannelContentGridItem
                            key={`${item.id}-${item.model_type}`}
                            item={item}
                            items={query.items}
                        />
                    ))}
                </ContentGrid>
            )}

            <InfiniteScrollSentinel query={query} />
        </div>
    );
}
